<template>
  <transition name="edit-fade" mode="out-in">
    <div class="columns pages-wrapper" v-if="!edit && !add" :key="'1'">
      <div class="column is-8 pages">
        <div class="field filter">
          <p class="control has-icons-right">
            <input class="input" type="text" :placeholder="$resx('Global.Filter')" v-model="input">
            <span class="icon is-small is-right" @click.prevent="input = ''">
              <font-awesome-icon :icon="icons.delete" />
            </span>
          </p>
        </div>
        <div class="pages-header">
          <span v-for="(key, idx) in pageKeys" :key="idx" @click="input = (input === '' ? key.toLowerCase() : (input === key.toLowerCase() ? '' : key.toLowerCase()))" :class="{'active': input.toLowerCase() === key.toLowerCase()}">
            {{ $resx("Page." + key) }}
          </span>
        </div>
        <draggable class="pages-list items-list" :tag="'ul'" @start="drag = true" @end="drag = false" :disabled="input !== ''" v-model="allPages" v-bind="dragOptions">
          <transition-group type="transition" :name="!drag ? 'flip-list' : null">
            <li v-for="(page, idx_l) in filtered" :key="idx_l+1" @click="selectPage(idx_l)" :class="{selected: selected === idx_l}">
              <div class="page">
                <font-awesome-icon :icon="page.ID === $store.state.homepage ? icons.home : icons.homeLight" @click.stop="$store.dispatch('setHomepage', page.ID)" />
                {{page.ID}}
              </div>
              <div class="cultures">
                <span v-for="(culture, key) in page.Cultures" :key="key" :class="['flag flag-xs flag-'+key.split('-')[1].toLowerCase()]"></span>
              </div>
              <div class="settings">
                <font-awesome-icon v-for="(key, idx_s) in pageKeys" :key="idx_s" :icon="page[key] ? icons.check : icons.times" />
              </div>
            </li>
          </transition-group>
        </draggable>
      </div>
      <div class="column is-4">
        <div class="buttons">
          <button class="button is-fullwidth" @click.prevent="addPage">
            <span class="icon">
              <font-awesome-icon :icon="icons.add"/>
            </span>
            <span>{{ $resx("Buttons.Add") }}</span>
          </button>
          <button class="button is-fullwidth" :disabled="(selected > -1 ? false : 'disabled')" @click.prevent="edit = true, $emit('canClose', false), $store.commit('contentChanged', true)">
            <span class="icon">
              <font-awesome-icon :icon="icons.edit" />
            </span>
            <span>{{ $resx("Buttons.Edit") }}</span>
          </button>
          <button class="button is-fullwidth is-danger" :disabled="(selected > -1 ? false : 'disabled')" @click.prevent="deletePage">
            <span class="icon">
              <font-awesome-icon :icon="icons.delete" />
            </span>
            <span>{{ $resx("Buttons.Delete") }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="columns form-wrapper" v-else :key="'2'">
      <loader :loading="loading" :fixed="false"></loader>
      <form class="column is-8">
        <div class="field">
          <label class="label">{{ $resx("Page.ID") }}</label>
          <div class="control">
            <input class="input" type="text" ref="pageId" v-bind:value="staticPage.ID" :disabled="(add ? false : 'disabled')" v-on:input="staticPage.ID = $event.target.value" />
          </div>
        </div>
        <div class="columns">
          <div class="field column is-4" v-for="(key, idx_s) in pageKeys" :key="idx_s">
            <label class="label">{{ $resx("Page." + key) }}</label>
            <div class="control">
              <div class="field has-addons">
                <p class="control">
                  <button class="button" :class="{'is-success': staticPage[key] }" @click.prevent="staticPage[key] = true">
                    <span>{{ $resx("Buttons.On") }}</span>
                  </button>
                </p>
                <p class="control">
                  <button class="button" :class="{'is-danger': !staticPage[key] }" @click.prevent="staticPage[key] = false">
                    <span>{{ $resx("Buttons.Off") }}</span>
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="tabs">
          <ul>
            <li :class="{'is-active': tabCulture === key}" v-for="(culture, key) in staticPage.Cultures" :key="key">
              <a @click.prevent="tabCulture = key">
                <span class="icon" :class="['flag flag-xs flag-'+key.split('-')[1].toLowerCase()]"></span>
              </a>
            </li>
            <li :disabled="(Object.keys(cultures).length > 0 ? false : 'disabled')">
              <a class="select">
                <select @change="addCulture">
                  <option selected disabled :value="false">{{ $resx("Buttons.Add") }}</option>
                  <option v-for="(culture, idx) in cultures" :value="idx" :key="idx">{{ culture }}</option>
                </select>
              </a>
            </li>
          </ul>
        </div>
        <transition name="tab-fade" mode="out-in">
          <div v-if="tabCulture != ''" :key="tabCulture">
            <div class="has-text-right">
              <font-awesome-icon :icon="icons.trash" @click="deleteStaticCulture(tabCulture)" />
            </div>
            <div class="field" v-for="(item, key) in staticPage.Cultures[tabCulture].Meta" :key="key">
              <label class="label" v-if="key !== 'Image'">{{ $resx("Page." + key) }}</label>
              <file-input v-if="key === 'Image' && (item || item === '')" :value.sync="item" @complete="setImage" :profile="'meta'" :disabledProfiles="['header', 'inline', 'fullwidth', 'image', 'icon', 'avatar', 'partner', 'theme']"></file-input>
              <div class="control" v-else>
                <input class="input" type="text" v-model="staticPage.Cultures[tabCulture].Meta[key]" @input="maxChars($event, key)" />
              </div>
            </div>
          </div>
        </transition>
      </form>
      <div class="column is-4">
        <div class="buttons">
          <button class="button is-fullwidth is-success" @click.prevent="submitPage('submit')" :disabled="(submitEnabled ? false : 'disabled')">
            <span>{{ $resx("Buttons.Save") }}</span>
          </button>
          <button class="button is-fullwidth" @click.prevent="submitPage('cancel')">
            <span>{{ $resx("Buttons.Cancel") }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>

import fileInput from '@/components/admin/FileInput.vue'
import { faPlus, faPencil, faTimes, faHome as faHomeLight } from '@fortawesome/pro-regular-svg-icons'
import { faHome, faCheck, faTimes as faTimesSolid, faTrashAlt } from '@fortawesome/pro-solid-svg-icons'
import draggable from 'vuedraggable'
import { getFilenameFromUrl } from '@/mixins/getFilenameFromUrl.js'
import { confirm } from '@/mixins/confirm.js'
import loader from '@/components/Loader.vue'

export default {
  name: 'modalContentPages',
  data () {
    return {
      loading: false,
      pageKeys: ['InMain', 'InFooter', 'InLinks'],
      selected: -1,
      input: '',
      drag: false,
      icons: {
        add: faPlus,
        edit: faPencil,
        check: faCheck,
        times: faTimesSolid,
        delete: faTimes,
        trash: faTrashAlt,
        home: faHome,
        homeLight: faHomeLight
      },
      add: false,
      edit: false,
      staticPage: false,
      tabCulture: '',
      selectedForDelete: [],
      templates: {
        Meta: {
          Name: '',
          Title: '',
          Description: '',
          Image: ''
        },
        Culture: {
          Meta: {
            Name: '',
            Title: '',
            Description: '',
            Image: ''
          },
          Content: {
            ContentBlocks: [],
            Header: {
              Align: 'Left',
              CallToAction: {
                Text: '',
                Link: ''
              },
              Image: '',
              Subtitle: '',
              Title: ''
            },
            Html: '',
            ShowMenu: false
          }
        },
        Page: {
          ID: '',
          InMain: false,
          InFooter: false,
          InLinks: false,
          Cultures: {}
        }
      }
    }
  },
  mixins: [confirm, getFilenameFromUrl],
  components: {
    draggable,
    fileInput,
    loader
  },
  computed: {
    staticCulturesLength () {
      return Object.keys(this.staticPage.Cultures).length
    },
    allPages: {
      get () {
        return this.$store.state.sitemap
      },
      set (value) {
        // update page orders PUT orderArr
        var self = this
        self.$store.commit('setSitemap', value)
        var orderArr = []
        value.forEach(page => {
          orderArr.push(page.ID)
        })
        // PUT page order
        self.$http({
          method: 'PUT',
          url: '/pages/order',
          name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
          api: 'website',
          data: orderArr
        }).then(function () {}).catch(function (err) {
          self.$store.dispatch('setSnack', {
            message: err.message
          })
        })
      }
    },
    cultures () {
      var tmp = JSON.parse(JSON.stringify(this.$store.state.cultures))
      if (this.staticPage.Cultures) {
        var keys = Object.keys(this.staticPage.Cultures)
        for (var i = 0; i < keys.length; i++) {
          if (tmp.hasOwnProperty(keys[i])) { delete tmp[keys[i]] }
        }
        return tmp
      } else {
        return tmp
      }
    },
    submitEnabled () {
      if (this.add) {
        return this.staticPage.ID !== ''
      } else {
        return !(JSON.stringify(this.staticPage) === JSON.stringify(this.filtered[this.selected]))
      }
    },
    selectedPage () {
      if (this.selected > -1) { return this.filtered[this.selected] } else { return false }
    },
    filtered () {
      var filtered = []; var self = this; var input = self.input.toLowerCase()
      self.$store.state.sitemap.forEach(page => {
        if (page.ID.toLowerCase().indexOf(input) > -1 || (page.InMain && input === 'inmain') || (page.InFooter && input === 'infooter') || (page.InLinks && input === 'inlinks')) { filtered.push(page) }
      })
      this.unselectPage()
      return filtered
    },
    dragOptions () {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },
  methods: {
    maxChars: function (e, key) {
      var value = e.target.value; var max = 200

      switch (key) {
        case 'Name':
          max = 20
          break
      }

      if (value.length >= max) {
        value = value.substring(0, max)
        this.staticPage.Cultures[this.tabCulture].Meta[key] = value
      }
    },
    unselectPage: function () {
      this.selected = -1
    },
    setImage: function (data) {
      if (this.tabCulture !== '') {
        this.staticPage.Cultures[this.tabCulture].Meta.Image = data.url
      }
    },
    selectPage: function (idx) {
      if (this.selected === idx) {
        this.tabCulture = ''
        this.selected = -1
        this.staticPage = false
      } else {
        this.selected = idx
        this.tabCulture = Object.keys(this.filtered[this.selected].Cultures)[0] || ''
        this.staticPage = JSON.parse(JSON.stringify(this.filtered[this.selected]))
      }
    },
    addCulture: function (e) {
      var self = this
      var key = e.target.value
      if (key !== 'false') {
        e.target.selectedIndex = 0
        self.staticPage.Cultures[key] = {}
        self.staticPage.Cultures[key].Meta = JSON.parse(JSON.stringify(self.templates.Meta))
        self.tabCulture = key
        self.staticPage = Object.assign({}, self.staticPage)
      }
    },
    addPage: function () {
      this.tabCulture = ''
      this.staticPage = this.templates.Page
      this.add = true
      this.$emit('canClose', false)
      this.$store.commit('contentChanged', true)
    },
    deleteStaticCulture: function (key) {
      var self = this
      var tmp = JSON.parse(JSON.stringify(self.staticPage))
      if (tmp.Cultures.hasOwnProperty(key)) {
        delete tmp.Cultures[key]
        var keys = Object.keys(tmp.Cultures)
        if (keys.length > 0) {
          self.tabCulture = keys[0]
        }
        self.staticPage = tmp
      }
    },
    deletePage: function () {
      var self = this; var message
      if (self.selected > -1) {
        message = self.$resx('Global.AreYouSure')
        self.confirm({
          isTemplate: false,
          message: message,
          buttons: [
            {
              name: self.$resx('Buttons.Cancel'),
              class: 'is-pulled-left'
            },
            {
              name: self.$resx('Buttons.Delete'),
              class: 'is-pulled-right is-danger',
              action: function () {
                var page = self.filtered[self.selected].ID
                self.$store.dispatch('deletePage', page)
                self.$http({
                  method: 'DELETE',
                  url: '/pages/' + page,
                  api: 'website',
                  name: process.env.VUE_APP_NAME + '_' + self.$store.state.language
                  // data: Object.keys(self.staticPage.Cultures)
                }).then(function (response) {
                  self.$store.dispatch('deletePage', page)
                  self.$store.dispatch('setSnack', {
                    message: self.$resx('Page.Removed')
                  })
                  self.selected = -1
                }).catch(function (err) {
                  self.$store.dispatch('setSnack', {
                    message: err.message
                  })
                  self.selected = -1
                })
              }
            }
          ]
        })
      }
    },
    resetSelected: function () {
      var self = this
      self.staticPage = false
      self.selected = -1
      self.add = false
      self.edit = false
      self.$emit('canClose', true)
      self.$store.commit('contentChanged', false)
    },
    setCursorPos: function (el, pos) {
      if (el.createTextRange) {
        var range = el.createTextRange()
        range.move('character', pos)
        range.select()
      } else {
        if (el.selectionStart) {
          el.focus()
          el.setSelectionRange(pos, pos)
        } else { el.focus() }
      }
    },
    submitPage: function (action) {
      var self = this; var key
      if (action === 'submit') {
        // save page
        self.loading = true
        var method; var url = '/pages'; var api = 'website'
        if (self.add) {
          method = 'POST'
        } else {
          method = 'PUT'
        }
        var tmp = JSON.parse(JSON.stringify(self.staticPage))
        var promises = []
        var dontDelete = []
        var selected = self.filtered[self.selected] || JSON.parse(JSON.stringify(self.templates.Page))
        var deleteImages = []

        new Promise(function (resolve) {
          var general = JSON.parse(JSON.stringify(tmp))

          if (self.add || general.InMain !== selected.InMain || general.InFooter !== selected.InFooter || general.InLinks !== selected.InLinks) {
            delete general.Cultures
            resolve(self.$http({
              method: method,
              url: url,
              api: api,
              data: general,
              name: process.env.VUE_APP_NAME + '_' + self.$store.state.language
            }).catch(function (err) {
              return err
            }))
          } else {
            resolve()
          }
        }).then(function (error) {
          if (error && error.response && error.response.status === 500) {
            self.$store.dispatch('setSnack', {
              message: self.$resx('Global.AlreadyExists', ['ID'])
            })
            self.loading = false
            return false
          }
          for (key in tmp.Cultures) {
            var cultureStr = JSON.stringify(tmp.Cultures[key])
            var selectedStr = JSON.stringify(selected.Cultures[key])

            if (selected.Cultures.hasOwnProperty(key) && self.staticPage.Cultures[key].Meta.Image !== selected.Cultures[key].Meta.Image && selected.Cultures[key].Meta.Image !== '') {
              deleteImages.push(selected.Cultures[key].Meta.Image)
            }

            if (!selected.Cultures.hasOwnProperty(key) || self.add) {
              delete tmp.Cultures[key].Content
              promises.push(self.$http({
                method: 'POST',
                url: url + '/meta/' + tmp.ID,
                api: api,
                data: tmp.Cultures[key].Meta,
                name: process.env.VUE_APP_NAME + '_' + key
              }))
            } else if (cultureStr !== selectedStr) {
              delete tmp.Cultures[key].Content
              promises.push(self.$http({
                method: 'PUT',
                url: url + '/meta/' + tmp.ID,
                api: api,
                data: tmp.Cultures[key].Meta,
                name: process.env.VUE_APP_NAME + '_' + key
              }))
            }
            dontDelete.push(key)
            delete tmp.Cultures[key]
          }
          for (key in selected.Cultures) {
            if (dontDelete.indexOf(key) === -1) {
              promises.push(self.$http({
                method: 'DELETE',
                url: url + '/content/' + tmp.ID,
                name: process.env.VUE_APP_NAME + '_' + key,
                api: api
              }))
            }
          }
          for (var i = 0; i < deleteImages.length; i++) {
            var file = self.getFilenameFromUrl(deleteImages[i])
            self.$store.dispatch('deleteFile', file)
          }

          self.$axios.all(promises).then(function (data) {
            self.$store.dispatch('getSitemap', true).then(function () {
              self.loading = false
              self.$store.dispatch('setSnack', {
                message: self.$resx('Page.Saved')
              })
              self.resetSelected()
            })
          }).catch(function (err) {
            self.$store.dispatch('setSnack', {
              message: err.message
            })
            self.loading = false
            self.resetSelected()
          })
        })
      } else {
        if (self.submitEnabled) {
          // if image is changed but the action is canceled, delete the image
          for (key in self.staticPage.Cultures) {
            if (self.selectedPage.Cultures.hasOwnProperty(key) && self.staticPage.Cultures[key].Meta.Image !== '' && self.staticPage.Cultures[key].Meta.Image !== self.selectedPage.Cultures[key].Meta.Image) {
              var file2 = self.getFilenameFromUrl(self.staticPage.Cultures[key].Meta.Image)
              self.$store.dispatch('deleteFile', file2)
            }
          }
        }
        self.resetSelected()
      }
    }
  },
  watch: {
    'staticPage.ID' (value, old) {
      var self = this
      if (self.add && value) {
        if (value.length > 0) {
          var chars = [...value]
          var caret = -1
          for (var i = 0; i < chars.length; i++) {
            if (!(/([a-zA-Z0-9-])/.test(chars[i]))) {
              value = value.substr(0, i) + '-' + value.substr(i + 1)
              caret = i
            }
          }
          value = value.charAt(0).toUpperCase() + value.slice(1)
          self.staticPage.ID = value
          if (caret > -1) {
            self.$nextTick(() => {
              self.setCursorPos(self.$refs.pageId, (!old || (value.length - old.length > 1) ? value.length : caret + 1))
            })
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.edit-fade-enter-active,
.edit-fade-leave-active,
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.2s;
}
.edit-fade-enter,
.edit-fade-leave-to,
.tab-fade-enter,
.tab-fade-leave-to {
  opacity: 0;
}
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.3;
}

.pages-wrapper{
  height: 450px;

  .pages{
    .filter{
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 180px);
      margin-right: 10px;
      margin-bottom: 0;

      .control{
        .icon{
          pointer-events: all;
          cursor: pointer !important;
        }
      }
    }

    .pages-header{
      display: inline-block;
      vertical-align: bottom;
      width: 150px;
      span{
        display: inline-block;
        vertical-align: top;
        width: 50px;
        text-align: center;
        font-size: 12px;
        cursor: pointer;
        border-radius: 4px;

        &.active{
          background-color: $main;
          color: $white;
        }
      }
    }

    .pages-list{
      // height: 380px;
      // overflow: auto;
      // margin-top: 10px;

      li{
        // height: auto;
        // display: block;
        // width: 100%;
        // border: 1px solid $grey-lighter;
        // background-color: $grey-bg;
        // border-radius: 4px;
        // padding: 10px 20px;
        // cursor: pointer;

        // + li{
        //   margin-top: 10px;
        // }

        // &.selected{
        //   border-color: $grey;
        // }

        .page{
          display: inline-block;
          vertical-align: middle;
          width: calc(60% - 75px);

          svg{
            // vertical-align: top;
            margin-right: 10px;
            margin-left: -4px;
            color: $grey-lighter;

            &:hover{
              color: $grey-light;
            }

            &[data-prefix="fas"]{
              color: $warning;
            }
          }
        }
        .cultures{
          display: inline-block;
          vertical-align: middle;
          width: calc(40% - 75px);
          span{
            vertical-align: middle;
            +span{
              margin-left: 10px;
            }
          }
        }
        .settings{
          display: inline-block;
          vertical-align: top;
          width: 150px;

          svg{
            display: inline-block;
            vertical-align: middle;
            width: 50px;
            margin-bottom: 0;
            color: $success;
            text-align: center;

            &[data-icon=times]{

              line-height: 10px;
              color: $danger;
            }
          }
        }
      }
    }
  }
}
.form-wrapper{
  >.is-8{
    height: 450px;
    overflow: auto;

    .tabs{
      li{
        &[disabled]{
          opacity: 0.6;
          pointer-events: none;
        }
        a.select{
          height: 39px;

          select{
            width: 80px;
            border: none;
            padding-top: 0;
            padding-bottom: 0;
            padding-right: 0;
            padding-left: 0;
            height: 26px;
            &:focus{
              border: none;
              box-shadow: none;
            }
          }
        }
      }
    }
    .has-text-right{
      [data-icon=trash-alt]{
        opacity: 0.6;
        &:hover{
          cursor: pointer;
          opacity: 1;
        }
      }
    }
  }
}
</style>
